import React, { useState } from "react";

// Libs
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Contexts
import { useModal } from '../contexts/ModalContext';
import { useTheme } from '../contexts/ThemeContext';
import { useDevMode } from '../contexts/DevModeContext';

// Utils
import { implementedNodes } from '../utils/devModeUtils';

// Styles
import "~/styles/NodeDrag.css";

export const NodeDrag = ({ icon, title, nodeType, size, style, category }) => {
  const [dragging, setDragging] = useState(false);
  const [hover, setHover] = useState(false);
  const { showDevelopmentModal } = useModal();
  const { isRfTest } = useTheme();
  const { devMode } = useDevMode();

  const effectiveType = nodeType || title;
  
  // Verifica se o nó está implementado
  const isImplemented = implementedNodes.includes(effectiveType);
  const isDev = !isImplemented;
  
  // Se não está em modo dev e o nó não está implementado, não renderizar
  if (!devMode && isDev) {
    return null;
  }

  const onDragStart = (event, nodeType) => {
    // Bloqueia drag e mostra modal para qualquer tema se não implementado
    if (!isImplemented) {
      event.preventDefault();
      showDevelopmentModal(nodeType);
      return;
    }
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
    setDragging(true);
  };
  
  const onDragEnd = () => setDragging(false);

  const handleClick = () => {
    if (!isImplemented) {
      showDevelopmentModal(effectiveType);
    }
  };

  // Determinar categoria baseada no contexto para tema rf-test
  const getCategory = () => {
    if (category) return category;
    
    // Categorias baseadas no título para compatibilidade
    const comandosItems = ["Esperar", "Atribuir", "Ler", "Mostrar", "Fluxograma", "Ir para", "Repetir", "Decidir", "Inicio", "Fim"];
    const geometriaItems = ["Fundo", "Ponto", "Seg.Reta", "Reta", "Retang", "Elipse"];
    const avatarItems = ["Visível", "Posicionar", "Andar", "Virar", "Lápis", "Aparência", "Clicar", "Interagir Avatar"];
    const midiaItems = ["Música", "Som"];
    const operadoresItems = ["Multiplicação", "Divisão", "Soma", "Subtração", "Radiciação", "Potencialização", "Maior que", "Menor que", "Igual a", "Diferente", "Menor ou igual a", "Maior ou igual a", "Conjunção/Interseção", "Disjunção inclusiva/União", "Negação/complementação", "Disjunção exclusiva"];
    const valoresItems = ["Cor", "Imagem", "Áudio", "Verdadeiro|Falso", "Texto|Número"];
    const funcoesItems = ["Trigonom.", "Números", "Juntar", "Linear", "Aleatório"];
    
    if (comandosItems.includes(effectiveType) || geometriaItems.includes(effectiveType) || avatarItems.includes(effectiveType) || midiaItems.includes(effectiveType)) return "comandos";
    if (operadoresItems.includes(effectiveType)) return "operadores";
    if (valoresItems.includes(effectiveType)) return "valores";
    if (funcoesItems.includes(effectiveType)) return "funcoes";
    return "default";
  };

  const currentCategory = getCategory();

  const renderIcon = () => {
    if (typeof icon === 'string' && icon.startsWith('img:')) {
      const src = icon.slice('img:'.length);
      return (
        <img
          src={src}
          alt=""
          aria-hidden="true"
          style={{
            width: isRfTest ? 22 : 20,
            height: isRfTest ? 16 : 14,
            objectFit: 'contain',
            display: 'block'
          }}
        />
      );
    }

    if (typeof icon === 'string' && icon.startsWith('symbol:')) {
      const symbol = icon.slice('symbol:'.length);
      return (
        <span
          className="node-drag-symbol"
          aria-hidden="true"
          style={{
            fontSize: isRfTest ? '18px' : '16px',
            fontWeight: 800,
            lineHeight: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '20px',
            height: '20px'
          }}
        >
          {symbol}
        </span>
      );
    }

    return <FontAwesomeIcon icon={icon} className="icone" size={size || "1.2x"} />;
  };

  // Renderização condicional baseada no tema
  if (isRfTest) {
    // Design moderno (rf-test)
    return (
      <div
        className={`node-drag-container ${currentCategory} ${dragging ? 'dragging' : ''} ${hover ? 'hover' : ''} ${isDev ? 'not-implemented' : ''}`}
        onDragStart={(event) => onDragStart(event, effectiveType)}
        onDragEnd={onDragEnd}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={handleClick}
        draggable={isImplemented}
        style={{
          cursor: isImplemented ? (dragging ? 'grabbing' : 'grab') : 'pointer',
          ...style
        }}
      >
        <div className="node-drag-icon">
          {renderIcon()}
        </div>
        <span className="node-drag-title">{title}</span>
        {isDev && (
          <span className="dev-badge-modern" title="Em desenvolvimento">Em Dev</span>
        )}
      </div>
    );
  } else {
    // Design clássico (rf)
    return (
      <div
        className={`dnd-option ${category ? `dnd-${category}` : ''} ${!isImplemented ? 'dnd-option-not-implemented' : ''}`}
        onDragStart={(event) => onDragStart(event, effectiveType)}
        onDragEnd={onDragEnd}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={handleClick}
        draggable={isImplemented}
        style={{
          cursor: isImplemented 
            ? (dragging ? 'grabbing' : hover ? 'grab' : 'grab')
            : 'pointer',
          opacity: isImplemented ? 1 : 0.6,
          ...style
        }}
      >
        {typeof icon === 'string' && (icon.startsWith('img:') || icon.startsWith('symbol:'))
          ? renderIcon()
          : <FontAwesomeIcon icon={icon} className="icone" size={size} />}
        <span className="titulo">{title}</span>
        {!isImplemented && (
          <span className="dev-badge">Em Desenvolvimento</span>
        )}
      </div>
    );
  }
};
